import { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import parse from "html-react-parser"

import { post } from "shared/lib/api"
import classnames from "classnames"

import checkmark from "images/components/checkmark.svg?raw"
import styles from "./promo_code_form.module.scss"

const PromoCodeForm = ({
  disabled = false,
  isHidden = false,
  initialPromoCode,
  onValidCodeApplied,
  plans,
  promoPriceDetails,
  setPromoPriceDetails,
}) => {
  const [promoCode, setPromoCode] = useState(initialPromoCode)
  const [showInput, setShowInput] = useState(!!initialPromoCode)
  const [loading, setLoading] = useState(false)
  const [invalid, setInvalid] = useState(false)
  const inputRef = useRef()
  const toggleShow = () => setShowInput(!showInput)
  const discountApplied = !loading && promoPriceDetails
  const promoCodeInvalid = !loading && invalid

  const validate = async event => {
    event?.preventDefault()

    setLoading(true)
    try {
      post("/subscriptions/validate_promo_code", {
        code: promoCode,
        currency: plans.currency,
        month_price_in_cents: plans.monthlyPriceInCents,
        year_price_in_cents: plans.yearlyPriceInCents,
      })
        .then(result => {
          setPromoPriceDetails(result)
          setInvalid(false)
          onValidCodeApplied(promoCode)
        })
        .catch(() => setInvalid(true))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (showInput) {
      inputRef.current?.focus()
    } else {
      setPromoCode(null)
    }
  }, [showInput, inputRef])

  useEffect(() => {
    if (promoCode) {
      validate()
    }
  }, [])

  useEffect(() => {
    if (!initialPromoCode) {
      setPromoCode(null)
      setPromoPriceDetails(null)
      setLoading(false)
      inputRef.current?.focus()
    }
  }, [initialPromoCode])

  const promoCodeCopy = () => {
    if (plans.selectedInterval === "year" && promoPriceDetails.year_price_copy) {
      return promoPriceDetails.year_price_copy
    } else if (plans.selectedInterval === "month" && promoPriceDetails.month_price_copy) {
      return promoPriceDetails.month_price_copy
    } else {
      return "discount applied"
    }
  }

  return (
    <div
      className={classnames("field", styles["promo-code-field"], {
        [styles["success"]]: discountApplied,
        [styles["error"]]: promoCodeInvalid,
        [styles["hidden"]]: isHidden,
      })}
    >
      <div className="label">
        {!discountApplied && (
          <label>
            <a className="link-standalone" role="button" onClick={toggleShow}>
              Apply promo
            </a>
          </label>
        )}
      </div>
      <div
        className={classnames("input", styles["input-group"], {
          [styles["show"]]: showInput,
        })}
      >
        <div className={styles["input-line"]}>
          <input
            aria-label="promo code"
            ref={inputRef}
            type="text"
            disabled={disabled || loading || promoPriceDetails}
            value={promoCode || ""}
            onKeyDown={e => e.key === "Enter" && validate(e)}
            onChange={e => {
              setInvalid(false)
              setPromoPriceDetails(null)
              setPromoCode(e.target.value)
            }}
          />
          <input
            disabled={disabled || loading || !promoCode || promoPriceDetails}
            type="button"
            value="Apply"
            className="button-tertiary"
            onClick={validate}
          />
        </div>
        {discountApplied && (
          <div className={styles.infobox}>
            <span className={styles.checkmark}>{parse(checkmark)}</span>
            {promoCodeCopy()}
          </div>
        )}
        {promoCodeInvalid && <div className={styles.infobox}>Invalid promo code</div>}
      </div>
    </div>
  )
}

PromoCodeForm.propTypes = {
  disabled: PropTypes.bool,
  isHidden: PropTypes.bool,
  initialPromoCode: PropTypes.string,
  onValidCodeApplied: PropTypes.func.isRequired,
  plans: PropTypes.shape({
    currency: PropTypes.string.isRequired,
    monthlyPriceInCents: PropTypes.number.isRequired,
    selectedInterval: PropTypes.oneOf(["month", "year"]).isRequired,
    yearlyPriceInCents: PropTypes.number.isRequired,
  }),
  promoPriceDetails: PropTypes.object,
  setPromoPriceDetails: PropTypes.func,
}

PromoCodeForm.defaultProps = {
  initialPromoCode: "",
}

export default PromoCodeForm
